import { useRef, useState } from 'react';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import type {
  Sd844EmailTemplate,
  Sd844TemplateColumnSource,
  Sd844TemplateSampleSource,
  Sd844TemplateSheetPreview,
} from '@erp/rpc';
import { triggerExcelDownload } from 'src/utils/excelDownload';
import {
  applySuggestions,
  buildSaveTemplateInput,
  buildTestDraft,
  deriveMatchingStatus,
  deriveTemplateForm,
  draftFromSheet,
  suggestionReasons,
  toggleExcludedSheet,
  withColumnSource,
  reviewColumns,
  type PendingTemplateUpload,
  type TemplateDraft,
} from 'src/components/ship-and-debit/sd844TemplateEditorState';
import {
  useDeleteSd844EmailTemplate,
  useParseSd844TemplateUpload,
  usePreviewSd844EmailTemplate,
  useSaveSd844EmailTemplate,
  useSetSd844EmailTemplateEnabled,
  useSd844EmailTemplate,
  useSd844TemplateSampleValues,
  useSuggestSd844TemplateMappings,
} from './useSd844EmailTemplates';

/**
 * Controller for the per-supplier 844 email template editor.
 *
 * Holds the upload/draft state and every mutation, so the components stay
 * display-only and the rules can be read in one place instead of being spread
 * through JSX.
 */

export interface Sd844TemplateEditorController {
  template: Sd844EmailTemplate | null;
  pendingUpload: PendingTemplateUpload | null;
  form: TemplateDraft | null;
  isLoading: boolean;
  isParsing: boolean;
  isSaving: boolean;
  isPreviewing: boolean;
  isDeleting: boolean;
  isChangingEnabled: boolean;
  /**
   * Any write in flight. One flag for all of them: upload, save, preview and
   * delete overlap otherwise, and the persisted result then depends on which
   * request happens to finish last.
   */
  isBusy: boolean;
  /** Bindings edited since the last save — what Preview would NOT show. */
  hasUnsavedEdits: boolean;
  /** True while the matcher is proposing bindings for a fresh upload. */
  isSuggesting: boolean;
  /** Columns the matcher could not place — shown as "couldn't determine". */
  unresolved: Set<number>;
  /** How many bindings are still exactly as the matcher proposed them. */
  suggestedCount: number;
  /** Why each column got its proposal, for review. */
  reasons: Map<number, string>;
  /** Columns matched with less than certainty — flagged "Check this match". */
  uncertain: Set<number>;
  /** False for a viewer: everything is shown, nothing can be changed. */
  canEdit: boolean;
  /** Which real lines the sample values and test download use. */
  sampleSource: Sd844TemplateSampleSource;
  setSampleSource: (source: Sd844TemplateSampleSource) => void;
  /** columnIndex → the text that column would receive for one real line. */
  sampleValues: Map<number, string | null>;
  /** Invoice numbers the sample came from; `null` when there is no sample. */
  sampleInvoices: string[] | null;
  isLoadingSample: boolean;
  setExcludedSheet: (sheetName: string, excluded: boolean) => void;
  pickFile: (file: File) => Promise<void>;
  selectSheet: (sheet: Sd844TemplateSheetPreview) => void;
  setDataStartRow: (row: number) => void;
  setColumnSource: (
    columnIndex: number,
    source: Sd844TemplateColumnSource,
  ) => void;
  save: () => Promise<void>;
  setEnabled: (enabled: boolean) => Promise<void>;
  /** Render the form AS ON SCREEN against real lines and download it. */
  preview: () => Promise<void>;
  remove: () => Promise<void>;
}

/**
 * The sheet to bind first: the first one that is NOT our own default layout,
 * so a workbook whose "Example" tab comes first still opens on the claim sheet.
 */
function initialSheet(
  sheets: Sd844TemplateSheetPreview[],
): Sd844TemplateSheetPreview | undefined {
  return sheets.find((s) => !s.looksLikeDefaultLayout) ?? sheets[0];
}

export function useSd844TemplateEditor(
  companyId: string | null,
  enabled: boolean,
  canEdit = true,
): Sd844TemplateEditorController {
  const gt = useGT();

  const { data, isLoading } = useSd844EmailTemplate(companyId, enabled);
  const {
    parseUpload,
    saveTemplate,
    deleteTemplate,
    previewTemplate,
    suggestMappings,
    setTemplateEnabled,
    isBusy,
  } = useTemplateMutations();

  const template = data?.template ?? null;

  /** Set only while a freshly-dropped file is being bound, before saving. */
  const [pendingUpload, setPendingUpload] =
    useState<PendingTemplateUpload | null>(null);

  /**
   * Unsaved edits, or `null` when nothing has been touched since the last save.
   *
   * The saved template is DERIVED into the form rather than copied in by an
   * effect. A sync effect would let a background refetch overwrite bindings
   * someone was midway through setting — the bug the repo's "don't useEffect to
   * sync state" rule exists to prevent. Clearing this back to `null` after a
   * save is what re-binds the form to server state.
   */
  const [draft, setDraft] = useState<TemplateDraft | null>(null);

  /** Columns the matcher could not place, the ones it filled in, and why. */
  const {
    unresolved,
    setUnresolved,
    suggested,
    setSuggested,
    reasons,
    setReasons,
  } = useMatchingStatus();

  /**
   * Mirrors `draft` so a matching response can be applied to whatever the draft
   * is NOW, not the closure it was started with.
   */
  const draftRef = useRef<TemplateDraft | null>(null);

  /**
   * Identifies the matching run in flight. A response whose id is stale is
   * DISCARDED: bindings are matched to columns by index, and column 3 of one
   * sheet is not column 3 of another, so a late reply for a sheet the user has
   * already left would bind headers it never saw.
   */
  const matchRunRef = useRef(0);

  function setDraftTracked(next: TemplateDraft | null) {
    // Clearing the draft (a save or a delete completing) abandons any matching
    // run still in flight. Without this the reply passes the run-id check,
    // finds no current draft, falls back to the seeded one and RESTORES
    // mappings the user just saved away.
    if (next === null) matchRunRef.current += 1;
    draftRef.current = next;
    setDraft(next);
  }

  const form = deriveTemplateForm(draft, template);

  const sample = useTemplateSample(companyId, form, enabled);
  const { sampleSource } = sample;

  /**
   * Ask the matcher to fill in the bindings it can.
   *
   * Fire-and-forget against the seeded draft: the columns are already on screen
   * by this point, so a slow or failed proposal costs nothing but the
   * pre-filling. Only still-blank columns are touched, so a user who starts
   * picking while it runs cannot be overwritten.
   */
  async function proposeBindings(
    seeded: TemplateDraft,
    sheet: Sd844TemplateSheetPreview,
  ) {
    const runId = ++matchRunRef.current;
    setUnresolved(new Set());
    setSuggested(new Set());
    setReasons(new Map());
    try {
      // The team's row-2 notes and yellow highlights travel with each column:
      // they settle most bindings without a model call at all.
      const { suggestions } = await suggestMappings.mutateAsync({
        columns: sheet.columns.map(
          ({ columnIndex, headerLabel, hint, highlighted }) => ({
            columnIndex,
            headerLabel,
            ...(hint ? { hint } : {}),
            ...(highlighted ? { highlighted } : {}),
          }),
        ),
        hasHintRow: sheet.hintRow !== null,
      });

      // A newer sheet or upload started while this was in flight — these
      // bindings were computed against headers that are no longer on screen.
      if (matchRunRef.current !== runId) return;

      const base = draftRef.current ?? seeded;
      const applied = applySuggestions(base, suggestions);
      const status = deriveMatchingStatus(base, applied, suggestions);

      setDraftTracked(applied);
      setSuggested(status.suggested);
      setUnresolved(status.unresolved);
      setReasons(suggestionReasons(suggestions));
    } catch {
      if (matchRunRef.current !== runId) return;
      // Suggestions are a convenience; the editor still works without them.
      // Built from the CURRENT draft and blank columns only: someone who
      // started binding while the request was failing has already answered
      // those columns, and marking them "couldn't determine" would both
      // contradict the value on screen and inflate the outstanding count.
      setUnresolved(blankColumns(draftRef.current ?? seeded));
    }
  }

  async function pickFile(file: File) {
    // Read failures are handled separately from RPC failures. A FileReader
    // error is OUR string ("read failed") and means nothing to the user, so it
    // gets the localized fallback — whereas the server's validation text names
    // the actual problem ("no sheet named Sheet1") and is shown verbatim.
    const contentBase64 = await readFileOrReport(file, gt);
    if (contentBase64 === null) return;

    try {
      const { sheets } = await parseUpload.mutateAsync({
        filename: file.name,
        contentBase64,
      });
      const first = initialSheet(sheets);
      if (!first) {
        toast.error(
          gt('That workbook has no sheets.', {
            context: 'error when an uploaded 844 template file has no sheets',
          }),
        );
        return;
      }
      setPendingUpload({ filename: file.name, contentBase64, sheets });
      const seeded = draftFromSheet(first, sheets);
      setDraftTracked(seeded);
      void proposeBindings(seeded, first);
    } catch (error) {
      reportFailure(
        error,
        gt('That file could not be read as an Excel workbook.', {
          context: 'error when an uploaded 844 template cannot be parsed',
        }),
      );
    }
  }

  /** Every edit seeds the draft from whatever the form currently shows. */
  function editForm(change: Partial<TemplateDraft>) {
    if (form) setDraftTracked({ ...form, ...change });
  }

  async function save() {
    if (!form) return;
    try {
      await saveTemplate.mutateAsync(
        buildSaveTemplateInput({ companyId, form, pendingUpload }),
      );
      setPendingUpload(null);
      // Re-binds the form to the freshly-saved server state.
      setDraftTracked(null);
      toast.success(
        gt(
          '844 email template saved as disabled. Enable it when the mapping is ready.',
          {
            context: 'success toast after saving a supplier 844 email template',
          },
        ),
      );
    } catch (error) {
      reportFailure(
        error,
        gt('The template could not be saved.', {
          context: 'error toast when saving an 844 email template fails',
        }),
      );
    }
  }

  async function preview() {
    if (!form) return;
    try {
      const result = await previewTemplate.mutateAsync({
        companyId,
        sample: sampleSource,
        // Unsaved edits are rendered as they are on screen: checking a binding
        // must not require making it live first.
        ...(draft ? { draft: buildTestDraft(form, pendingUpload) } : {}),
      });
      triggerExcelDownload(result.contentBase64, result.filename);
      if (result.lineCount === 0)
        toast.warning(noSampleMessage(gt, sampleSource));
    } catch (error) {
      reportFailure(
        error,
        gt('The preview could not be generated.', {
          context: 'error toast when an 844 template preview fails',
        }),
      );
    }
  }

  async function setEnabled(nextEnabled: boolean) {
    if (!template) return;
    try {
      await setTemplateEnabled.mutateAsync({
        companyId,
        enabled: nextEnabled,
        expectedUpdatedAt: template.updatedAt,
      });
      toast.success(
        nextEnabled
          ? gt('844 email template enabled.', {
              context: 'success toast after enabling an 844 email template',
            })
          : gt(
              '844 email template disabled. Claims will use the fallback template.',
              {
                context: 'success toast after disabling an 844 email template',
              },
            ),
      );
    } catch (error) {
      reportFailure(
        error,
        gt('The template status could not be changed.', {
          context:
            'error toast when enabling or disabling an 844 template fails',
        }),
      );
    }
  }

  async function remove() {
    try {
      await deleteTemplate.mutateAsync({ companyId });
      setPendingUpload(null);
      setDraftTracked(null);
      toast.success(
        gt(
          'Template removed. This supplier reverts to the default attachment.',
          {
            context:
              'success toast after removing a supplier 844 email template',
          },
        ),
      );
    } catch (error) {
      reportFailure(
        error,
        gt('The template could not be removed.', {
          context: 'error toast when removing an 844 email template fails',
        }),
      );
    }
  }

  return {
    template,
    pendingUpload,
    form,
    isLoading,
    isParsing: parseUpload.isPending,
    isSaving: saveTemplate.isPending,
    isPreviewing: previewTemplate.isPending,
    isDeleting: deleteTemplate.isPending,
    isChangingEnabled: setTemplateEnabled.isPending,
    isBusy,
    hasUnsavedEdits: draft !== null,
    isSuggesting: suggestMappings.isPending,
    unresolved,
    suggestedCount: suggested.size,
    reasons,
    uncertain: form ? reviewColumns(form) : new Set(),
    canEdit,
    ...sample,
    setExcludedSheet: (sheetName, excluded) => {
      if (form) setDraftTracked(toggleExcludedSheet(form, sheetName, excluded));
    },
    pickFile,
    selectSheet: (sheet) => {
      // Re-runs matching for the newly chosen sheet: its headers are different,
      // so the previous sheet's bindings and statuses mean nothing here.
      const seeded = draftFromSheet(sheet, pendingUpload?.sheets ?? [sheet]);
      setDraftTracked(seeded);
      void proposeBindings(seeded, sheet);
    },
    setDataStartRow: (row) => editForm({ dataStartRow: row }),
    setColumnSource: (columnIndex, source) => {
      if (!form) return;
      // Touching a column makes it the user's choice, not the matcher's.
      setSuggested((current) => withoutColumn(current, columnIndex));
      setUnresolved((current) => withoutColumn(current, columnIndex));
      setReasons((current) => withoutReason(current, columnIndex));
      setDraftTracked(withColumnSource(form, columnIndex, source));
    },
    save,
    setEnabled,
    preview,
    remove,
  };
}

/**
 * Every mutation the editor drives, and whether any write is in flight.
 *
 * One `isBusy` for all of them: upload, save, preview and delete overlap
 * otherwise, and the persisted result then depends on which finishes last.
 */
function useTemplateMutations() {
  const parseUpload = useParseSd844TemplateUpload();
  const saveTemplate = useSaveSd844EmailTemplate();
  const deleteTemplate = useDeleteSd844EmailTemplate();
  const previewTemplate = usePreviewSd844EmailTemplate();
  const suggestMappings = useSuggestSd844TemplateMappings();
  const setTemplateEnabled = useSetSd844EmailTemplateEnabled();
  return {
    parseUpload,
    saveTemplate,
    deleteTemplate,
    previewTemplate,
    suggestMappings,
    setTemplateEnabled,
    isBusy:
      parseUpload.isPending ||
      saveTemplate.isPending ||
      previewTemplate.isPending ||
      deleteTemplate.isPending ||
      suggestMappings.isPending ||
      setTemplateEnabled.isPending,
  };
}

/** What the matcher did per column: couldn't place it, filled it, and why. */
function useMatchingStatus() {
  const [unresolved, setUnresolved] = useState<Set<number>>(new Set());
  const [suggested, setSuggested] = useState<Set<number>>(new Set());
  const [reasons, setReasons] = useState<Map<number, string>>(new Map());
  return {
    unresolved,
    setUnresolved,
    suggested,
    setSuggested,
    reasons,
    setReasons,
  };
}

/**
 * Which real lines the editor samples, and what each binding gives for one of
 * them. Its own hook so the editor controller stays about the draft.
 */
function useTemplateSample(
  companyId: string | null,
  form: TemplateDraft | null,
  enabled: boolean,
) {
  const [sampleSource, setSampleSource] =
    useState<Sd844TemplateSampleSource>('staged');
  const query = useSd844TemplateSampleValues({
    companyId,
    sample: sampleSource,
    columnMappings: form?.mappings ?? [],
    enabled: enabled && !!form,
  });
  return {
    sampleSource,
    setSampleSource,
    sampleValues: new Map(
      (query.data?.values ?? []).map((v) => [v.columnIndex, v.value]),
    ),
    sampleInvoices: query.data?.sample?.invoiceNumbers ?? null,
    isLoadingSample: query.isFetching,
  };
}

/** Columns still bound to nothing. */
export function blankColumns(draft: TemplateDraft): Set<number> {
  const columns = new Set<number>();
  for (const mapping of draft.mappings) {
    if (mapping.source.kind === 'blank') columns.add(mapping.columnIndex);
  }
  return columns;
}

/** The same Set without `key` — the same object when it is absent. */
function withoutColumn(current: Set<number>, key: number): Set<number> {
  if (!current.has(key)) return current;
  const next = new Set(current);
  next.delete(key);
  return next;
}

/** The same Map without `key` — the same object when it is absent. */
function withoutReason(
  current: Map<number, string>,
  key: number,
): Map<number, string> {
  if (!current.has(key)) return current;
  const next = new Map(current);
  next.delete(key);
  return next;
}

/** Why a test download came back empty, naming the other sample to try. */
function noSampleMessage(
  gt: ReturnType<typeof useGT>,
  source: Sd844TemplateSampleSource,
): string {
  return source === 'staged'
    ? gt(
        'This supplier has no staged transactions right now, so the file shows the empty form. Try “Latest sent claim”.',
        {
          context:
            'warning when a test 844 template download found no staged lines',
        },
      )
    : gt(
        'This supplier has no sent email claims yet, so the file shows the empty form.',
        {
          context:
            'warning when a test 844 template download found no sent lines',
        },
      );
}

/** Report the server's message when it has one — it names the real problem. */
function reportFailure(error: unknown, fallback: string) {
  toast.error(error instanceof Error ? error.message : fallback);
}

/**
 * Read a picked file, or say why not and return `null`. A FileReader error is
 * OUR string ("read failed") and means nothing to the user, so it gets the
 * localized message — unlike the server's validation text, shown verbatim.
 */
async function readFileOrReport(
  file: File,
  gt: ReturnType<typeof useGT>,
): Promise<string | null> {
  try {
    return await fileToBase64(file);
  } catch {
    toast.error(
      gt('That file could not be read.', {
        context: 'error when the browser cannot read a picked template file',
      }),
    );
    return null;
  }
}

/** Read a picked file as base64, without the data-URL prefix. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      // readAsDataURL always yields a string; the union covers the other read
      // methods, so reject rather than coerce an ArrayBuffer into one.
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the file as text'));
        return;
      }
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}
